import { z } from "zod";
import { email, money, pageQuery, phone, trimmed, uuid } from "./common";

const blankToNull = (v: unknown) => (typeof v === "string" && v.trim() === "" ? null : v);
const optText = (max: number) => z.preprocess(blankToNull, z.string().trim().max(max, { error: `Максимум ${max} символов` }).nullable().optional());

// ───────── поставщики (ТЗ 08 §8.7) ─────────

/** Карточка поставщика: общий для всех филиалов компании; срок оплаты — дней отсрочки от даты закупки. */
const supplierFields = z.strictObject({
  name: trimmed(1, 200),
  legalName: optText(200),
  taxId: optText(40),
  phone: z.preprocess(blankToNull, phone.nullable().optional()),
  email: z.preprocess(blankToNull, email.nullable().optional()),
  address: optText(300),
  contactPerson: optText(120),
  paymentTermsDays: z.number({ error: "Укажите число дней" }).int({ error: "Целое число дней" }).min(0, { error: "Не меньше 0" }).max(365, { error: "Не больше 365" }),
  notes: optText(1000),
  isActive: z.boolean(),
});
export const supplierSchema = supplierFields.extend({ paymentTermsDays: supplierFields.shape.paymentTermsDays.default(0), isActive: supplierFields.shape.isActive.default(true) });
/** Без значений по умолчанию: в PATCH не переданное поле не меняется (`.partial()` сохраняет `.default()`). */
export const supplierUpdateSchema = supplierFields.partial().extend({ version: z.number().int().min(1) }).strict();
export type SupplierInput = z.output<typeof supplierSchema>;

export const SUPPLIER_SEGMENTS = ["debt", "overdue", "inactive"] as const;
export const suppliersQuery = pageQuery.extend({
  q: z.string().trim().max(100).optional(),
  segment: z.enum(SUPPLIER_SEGMENTS).optional(),
});

// ───────── оплаты поставщикам (FR-PRC-007, BR-125) ─────────

/** cash — из открытой смены веб-кассы; bank — перевод со счёта (номер платёжки в `reference`). */
export const SUPPLIER_PAY_METHODS = ["cash", "bank"] as const;
export type SupplierPayMethod = (typeof SUPPLIER_PAY_METHODS)[number];

/**
 * Оплата поставщику: FIFO по закупкам с ближайшим сроком либо конкретная закупка (`documentId`). Больше долга — только
 * с `advance` (BR-125): остаток — аванс, зачитывается в следующие закупки.
 */
export const supplierPaymentSchema = z
  .strictObject({
    amount: money.refine((v) => v > 0, { error: "Больше 0" }),
    method: z.enum(SUPPLIER_PAY_METHODS, { error: "Выберите способ" }),
    documentId: uuid.optional(),
    advance: z.boolean().optional(),
    reference: optText(100),
    comment: optText(300),
  })
  .refine((d) => !(d.advance && d.documentId), { error: "Аванс не привязывается к закупке", path: ["advance"] });
export type SupplierPaymentInput = z.output<typeof supplierPaymentSchema>;

/** Возврат денег от поставщика: не больше аванса (переплаты); наличные — в смену веб-кассы. */
export const supplierRefundSchema = z.strictObject({
  amount: money.refine((v) => v > 0, { error: "Больше 0" }),
  method: z.enum(SUPPLIER_PAY_METHODS, { error: "Выберите способ" }),
  reference: optText(100),
  comment: optText(300),
});

/** Акт сверки (FR-SUP-003): период включительно, не больше двух лет. */
export const supplierStatementQuery = z
  .object({ from: z.iso.date({ error: "Дата в формате ГГГГ-ММ-ДД" }), to: z.iso.date({ error: "Дата в формате ГГГГ-ММ-ДД" }) })
  .refine((q) => q.to >= q.from, { error: "Конец периода раньше начала", path: ["to"] })
  .refine((q) => Date.parse(q.to) - Date.parse(q.from) <= 731 * 86_400_000, { error: "Период не больше двух лет", path: ["to"] });
